'use client';

/**
 * Donor Details Form — Annual / One-time tabbed donation flow
 *
 * Design aligned with the original Alexandra Millennium Green form: a tab
 * toggle between annual and one-time, three preset amounts plus a custom
 * input, and a single Gift Aid checkbox. Donor name + email are always
 * required (we need them for a receipt and to pre-fill the GoCardless
 * Drop-in); house number + postcode become required when Gift Aid is
 * selected because HMRC needs them to claim.
 *
 * The legally-required HMRC declaration text is sent in the confirmation
 * email rather than shown on the form. See docs/HMRC_GIFT_AID_DECLARATION.md
 * for the consultation note about confirming this approach with HMRC.
 */

import { useState, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { XCircle, Loader2, Gift, CheckCircle2 } from 'lucide-react';
import { GoCardlessDropin } from './gocardless-dropin';
import { computeFeeCover, formatPence } from '@/lib/billing/fee-cover';
import { cn } from '@/lib/utils';

/**
 * Processing overlay shown while GoCardless popup/redirect is active.
 * After 15 seconds, adds a "Check payment status" button that polls our
 * own API as a fallback for when the GC SDK fails to fire callbacks.
 */
function ProcessingOverlay({
  charityLogoUrl,
  charityName,
  isEmbed,
  dropinFlowId,
  dropinDonationId,
  dropinStatusToken,
  customReturnUrl,
  donationType,
  effectiveAmount,
  onSuccess,
  onExit,
}: {
  charityLogoUrl?: string | null;
  charityName: string;
  isEmbed: boolean;
  dropinFlowId: string;
  dropinDonationId: string | null;
  dropinStatusToken: string | null;
  customReturnUrl?: string | null;
  donationType: 'recurring' | 'oneoff';
  effectiveAmount: number | null;
  giftAid: boolean;
  onSuccess: () => void;
  onExit: (err?: string) => void;
}) {
  // Auto-poll our own status endpoint every 3 seconds. The GC Drop-in SDK
  // handles callbacks correctly for one-off payments, but for DD the SDK
  // often loses track of the popup. Polling is the reliable fallback: the
  // webhook updates donation status to 'succeeded', and this poll picks it up.
  useEffect(() => {
    if (!dropinDonationId || !dropinStatusToken) return;

    const poll = async () => {
      try {
        const res = await fetch(
          `/api/donations/${dropinDonationId}/status?status_token=${encodeURIComponent(dropinStatusToken)}`
        );
        const data = await res.json();
        if (data.status === 'succeeded') {
          onSuccess();
        } else if (data.status === 'failed') {
          onExit('Payment failed. Please try again.');
        }
        // 'pending' → keep polling
      } catch {
        // Network glitch — keep polling, don't error out
      }
    };

    const initialDelay = setTimeout(poll, 1000);
    const interval = setInterval(poll, 1000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [dropinDonationId, dropinStatusToken, onSuccess, onExit]);

  const isAnnual = donationType === 'recurring';
  const amountLabel = effectiveAmount ? `£${effectiveAmount.toFixed(2)}${isAnnual ? '/year' : ''}` : '';

  return (
    <div className="donation-form">
      <div className={cn('rounded-xl border border-(--warm-slate-200) bg-white overflow-hidden', !isEmbed && 'shadow-warm-lg')}>
        <div className="px-5 py-12 text-center">
          {charityLogoUrl && (
            // eslint-disable-next-line @next/next/no-img-element -- charity logo is a user-supplied URL; the image optimizer is deliberately not exposed to remote input (see audit-ignores history)
            <img src={charityLogoUrl} alt="" className="h-10 w-auto max-w-[80px] object-contain mx-auto mb-4" />
          )}
          <Loader2 className="h-12 w-12 text-(--accent-gold) mx-auto mb-4 animate-spin" />
          <h2 className="text-[18px] font-medium text-(--warm-slate-800) mb-2" style={{ fontFamily: 'var(--font-fraunces, serif)' }}>
            Processing your payment
          </h2>
          <p className="text-[14px] text-(--warm-slate-500) mb-1">
            {amountLabel && `${amountLabel} to ${charityName}`}
          </p>
          <p className="text-[13px] text-(--warm-slate-400)">
            Please complete the payment in the GoCardless window.
            This page will update automatically once confirmed.
          </p>
        </div>
      </div>

      {dropinDonationId && (
        <GoCardlessDropin
          billingRequestFlowId={dropinFlowId}
          donationId={dropinDonationId}
          customReturnUrl={customReturnUrl}
          isEmbed={isEmbed}
          onSuccess={onSuccess}
          onExit={async (exitError) => {
            // GC SDK fires onExit when popup closes — but the payment may
            // have succeeded (SDK just didn't detect fulfillment in time).
            // Check actual status before resetting the form.
            if (dropinDonationId && dropinStatusToken) {
              try {
                const res = await fetch(
                  `/api/donations/${dropinDonationId}/status?status_token=${encodeURIComponent(dropinStatusToken)}`
                );
                const data = await res.json();
                if (data.status === 'succeeded') {
                  onSuccess();
                  return;
                }
              } catch { /* fall through to normal exit */ }
            }
            onExit(exitError);
          }}
        />
      )}
    </div>
  );
}

interface DonorDetailsFormProps {
  charityId: string;
  charityName: string;
  charityLogoUrl?: string | null;
  customReturnUrl?: string | null;
  isEmbed?: boolean;
}

type DonationType = 'recurring' | 'oneoff';

const ANNUAL_PRESETS = [10, 25, 50] as const;
const ONEOFF_PRESETS = [10, 25, 50] as const;

export function DonorDetailsForm({
  charityId,
  charityName,
  charityLogoUrl,
  customReturnUrl,
  isEmbed = false,
}: DonorDetailsFormProps) {
  // Selection state
  const [donationType, setDonationType] = useState<DonationType>('recurring');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(25);
  const [customAmount, setCustomAmount] = useState('');

  // Donor details (always captured)
  const [donorFirstName, setDonorFirstName] = useState('');
  const [donorLastName, setDonorLastName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');

  // Address fields (required only when Gift Aid is checked)
  const [donorHouseNumber, setDonorHouseNumber] = useState('');
  const [donorPostcode, setDonorPostcode] = useState('');
  const [giftAid, setGiftAid] = useState(false);

  // "Cover the fees" opt-in. When checked, the donor pays gross
  // (donation + GC fee + TrusteeKit app_fee if Gift Aid) so the charity
  // receives the donor's intended amount in full. For recurring, the
  // grossed-up amount becomes the recurring amount on every cycle —
  // donor consent applies to the inflated total.
  const [coverFees, setCoverFees] = useState(false);

  // UI state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GoCardless Drop-in state (set after server creates the billing request)
  const [dropinFlowId, setDropinFlowId] = useState<string | null>(null);
  const [dropinDonationId, setDropinDonationId] = useState<string | null>(null);
  const [dropinStatusToken, setDropinStatusToken] = useState<string | null>(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Effective amount, in pounds. selectedPreset wins over customAmount when both
  // somehow have values (defensive — UI prevents this state).
  const effectiveAmount = selectedPreset ?? (customAmount ? parseFloat(customAmount) : null);

  // Fee-cover calculation. Recomputes whenever the base amount or Gift
  // Aid choice changes — the TrusteeKit app_fee is Gift-Aid-only, so the
  // displayed cover amount must update when the donor toggles GA.
  const feeCover = useMemo(() => {
    if (!effectiveAmount || effectiveAmount < 1) return null;
    return computeFeeCover(Math.round(effectiveAmount * 100), giftAid);
  }, [effectiveAmount, giftAid]);

  // Gross amount actually charged to the donor (in pounds, for display
  // labels). When cover is off this equals the base amount.
  const chargedAmount =
    coverFees && feeCover ? feeCover.totalPence / 100 : effectiveAmount;

  const handleTypeChange = (type: DonationType) => {
    setDonationType(type);
    // Reset amount state so a £10 selection on Annual doesn't carry over to
    // One-time and confuse the donor about what they're committing to.
    setSelectedPreset(25);
    setCustomAmount('');
  };

  const handlePresetClick = (preset: number) => {
    setSelectedPreset(preset);
    setCustomAmount('');
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(v);
    if (v) setSelectedPreset(null);
  };

  const validate = (): string | null => {
    if (!effectiveAmount || effectiveAmount < 1) {
      return 'Please choose an amount of at least £1';
    }
    if (!donorFirstName || !donorLastName) {
      return 'Please enter your first and last name';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!donorEmail || !emailRegex.test(donorEmail)) {
      return 'Please enter a valid email address';
    }
    if (giftAid) {
      if (!donorHouseNumber.trim()) {
        return 'House number/name is required for Gift Aid';
      }
      const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
      if (!postcodeRegex.test(donorPostcode.trim())) {
        return 'Please enter a valid UK postcode for Gift Aid';
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setProcessing(true);

    try {
      const amountInPence =
        coverFees && feeCover
          ? feeCover.totalPence
          : Math.round(effectiveAmount! * 100);

      const response = await fetch('/api/donate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          charityId,
          amount: amountInPence,
          donorTitle: '',
          donorFirstName,
          donorLastName,
          donorEmail,
          donorHouseNumber: giftAid ? donorHouseNumber : '',
          donorAddressLine1: '',
          donorAddressLine2: '',
          donorCity: '',
          donorPostcode: giftAid ? donorPostcode.trim().toUpperCase() : '',
          giftAid,
          type: donationType,
          isEmbed,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }

      setDropinFlowId(data.billingRequestFlowId);
      setDropinDonationId(data.donationId);
      setDropinStatusToken(data.statusToken ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setProcessing(false);
    }
  };

  // Shared design tokens
  const inputClass = cn(
    'h-11 rounded-lg border bg-(--warm-slate-50) border-(--warm-slate-200)',
    'text-[15px] text-(--warm-slate-800) placeholder:text-(--warm-slate-400)',
    'focus:border-(--accent-gold-light) focus:ring-2 focus:ring-(--accent-gold-light)/10',
    'transition-all duration-200'
  );
  const labelClass = 'text-[13px] font-medium text-(--warm-slate-600) mb-1.5 block';

  // Scroll to top when transitioning to thank-you or processing state so
  // the donor sees the message from the top, not mid-scroll in the form.
  useEffect(() => {
    if (paymentComplete || (dropinFlowId && processing)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paymentComplete, dropinFlowId, processing]);

  // Thank-you state — shown inside the iframe when the Drop-in's onSuccess
  // fires (popup auto-closed, or Drop-in completed inline). Differentiates
  // annual vs one-off messaging so the donor understands what just happened.
  if (paymentComplete) {
    const donatedAmount = chargedAmount ?? effectiveAmount ?? 0;
    const isAnnual = donationType === 'recurring';
    return (
      <div className="donation-form">
        <div className={cn('rounded-xl border border-(--warm-slate-200) bg-white overflow-hidden', !isEmbed && 'shadow-warm-lg')}>
          <div className="px-5 py-8 text-center">
            {charityLogoUrl && (
              // eslint-disable-next-line @next/next/no-img-element -- charity logo is a user-supplied URL; the image optimizer is deliberately not exposed to remote input (see audit-ignores history)
              <img src={charityLogoUrl} alt="" className="h-12 w-auto max-w-[120px] object-contain mx-auto mb-3" />
            )}
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-[22px] font-medium text-(--warm-slate-800) mb-2" style={{ fontFamily: 'var(--font-fraunces, serif)' }}>
              Thank you!
            </h2>
            <p className="text-[15px] text-(--warm-slate-600) mb-4">
              {isAnnual ? (
                <>
                  Your annual donation of £{donatedAmount.toFixed(2)}/year to {charityName} has been set up.
                  Your first payment has been processed and a Direct Debit will collect future payments automatically.
                </>
              ) : (
                <>
                  Your donation of £{donatedAmount.toFixed(2)} to {charityName} has been submitted.
                </>
              )}
            </p>
            {giftAid && (
              <p className="text-[13px] text-amber-700 bg-amber-50 rounded-lg px-4 py-2 inline-block mb-4">
                <Gift className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Gift Aid will add 25% to your donation
              </p>
            )}
            <p className="text-[13px] text-(--warm-slate-500) mb-4">
              You will receive a receipt via email shortly.
            </p>
            {customReturnUrl && !isEmbed && (
              <a
                href={customReturnUrl}
                className="inline-block text-[13px] text-(--accent-gold) hover:text-(--accent-gold-hover) font-medium"
              >
                Return to {charityName}&apos;s website →
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // While the Drop-in is active (GoCardless popup or redirect in progress),
  // show a clean processing state instead of the full form with fields.
  // The donor has already submitted — showing the form again is confusing.
  //
  // Fallback: the GC Drop-in SDK sometimes fails to fire onSuccess/onExit
  // for the DD flow (popup navigates through multiple domains and the SDK
  // loses track). After 15 seconds, show a "Check payment status" button
  // that polls our own API. The donor isn't stuck.
  if (dropinFlowId && processing) {
    return (
      <ProcessingOverlay
        charityLogoUrl={charityLogoUrl}
        charityName={charityName}
        isEmbed={isEmbed}
        dropinFlowId={dropinFlowId}
        dropinDonationId={dropinDonationId}
        dropinStatusToken={dropinStatusToken}
        customReturnUrl={customReturnUrl}
        donationType={donationType}
        effectiveAmount={chargedAmount}
        giftAid={giftAid}
        onSuccess={() => setPaymentComplete(true)}
        onExit={(exitError) => {
          setDropinFlowId(null);
          setDropinDonationId(null);
          setDropinStatusToken(null);
          setProcessing(false);
          if (exitError) setError(exitError);
        }}
      />
    );
  }

  const submitLabel = (() => {
    if (processing) return 'Processing…';
    if (!effectiveAmount) return donationType === 'recurring' ? 'Set up annual donation' : 'Donate';
    const amountForLabel = chargedAmount ?? effectiveAmount;
    const formatted = `£${amountForLabel.toFixed(amountForLabel % 1 ? 2 : 0)}`;
    return donationType === 'recurring'
      ? `Set up ${formatted}/year`
      : `Donate ${formatted}`;
  })();

  const presets = donationType === 'recurring' ? ANNUAL_PRESETS : ONEOFF_PRESETS;

  return (
    <form onSubmit={handleSubmit} className="donation-form">
      <div className={cn('rounded-xl border border-(--warm-slate-200) bg-white overflow-hidden', !isEmbed && 'shadow-warm-lg')}>
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-(--warm-slate-100)">
          <div className="flex items-center gap-3">
            {charityLogoUrl && (
              // eslint-disable-next-line @next/next/no-img-element -- charity logo is a user-supplied URL; the image optimizer is deliberately not exposed to remote input (see audit-ignores history)
              <img src={charityLogoUrl} alt="" className="h-10 w-auto max-w-[80px] object-contain shrink-0" />
            )}
            <h2 className="text-[18px] font-medium text-(--warm-slate-800)" style={{ fontFamily: 'var(--font-fraunces, serif)' }}>
              Support {charityName}
            </h2>
          </div>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Type toggle */}
          <div className="grid grid-cols-2 rounded-lg border border-(--warm-slate-200) bg-(--warm-slate-50) p-1">
            <button
              type="button"
              onClick={() => handleTypeChange('recurring')}
              className={cn(
                'h-9 rounded-md text-[14px] font-medium transition-all',
                donationType === 'recurring'
                  ? 'bg-white text-(--warm-slate-800) shadow-sm'
                  : 'text-(--warm-slate-500) hover:text-(--warm-slate-700)'
              )}
            >
              Annual
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange('oneoff')}
              className={cn(
                'h-9 rounded-md text-[14px] font-medium transition-all',
                donationType === 'oneoff'
                  ? 'bg-white text-(--warm-slate-800) shadow-sm'
                  : 'text-(--warm-slate-500) hover:text-(--warm-slate-700)'
              )}
            >
              One-time
            </button>
          </div>

          {/* Section title (no subtitle per design intent) */}
          <div>
            <h3 className="text-[15px] font-medium text-(--warm-slate-700)">
              {donationType === 'recurring' ? 'Annual Donation' : 'One-time Donation'}
            </h3>
          </div>

          {/* Preset amounts */}
          <div className="grid grid-cols-3 gap-2">
            {presets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  'h-12 rounded-lg border text-[15px] font-medium transition-all',
                  selectedPreset === preset
                    ? 'border-(--accent-gold) bg-(--accent-gold-light)/10 text-(--warm-slate-800)'
                    : 'border-(--warm-slate-200) bg-white text-(--warm-slate-700) hover:border-(--warm-slate-300)'
                )}
              >
                £{preset}{donationType === 'recurring' ? '/yr' : ''}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div>
            <label className={labelClass} htmlFor="custom-amount">
              {donationType === 'recurring' ? 'Custom annual amount' : 'Custom amount'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-(--warm-slate-400) text-[15px] pointer-events-none">£</span>
              <Input
                id="custom-amount"
                type="text"
                inputMode="decimal"
                value={customAmount}
                onChange={handleCustomChange}
                placeholder={donationType === 'recurring' ? 'e.g. 75' : 'e.g. 30'}
                className={cn(inputClass, 'pl-7')}
              />
              {donationType === 'recurring' && customAmount && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-(--warm-slate-400) text-[13px]">/year</span>
              )}
            </div>
          </div>

          {/* Donor details — always required */}
          <div className="space-y-3 pt-2 border-t border-(--warm-slate-100)">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass} htmlFor="first-name">First name *</label>
                <Input id="first-name" type="text" autoComplete="given-name" value={donorFirstName} onChange={(e) => setDonorFirstName(e.target.value)} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass} htmlFor="last-name">Last name *</label>
                <Input id="last-name" type="text" autoComplete="family-name" value={donorLastName} onChange={(e) => setDonorLastName(e.target.value)} className={inputClass} required />
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor="email">Email *</label>
              <Input id="email" type="email" autoComplete="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} className={inputClass} required />
            </div>
          </div>

          {/* Gift Aid block */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={giftAid}
                onChange={(e) => setGiftAid(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="flex-1">
                <span className="block text-[14px] font-medium text-(--warm-slate-800)">
                  I&apos;m a UK taxpayer and want this charity to claim Gift Aid on my donation
                </span>
                <span className="mt-1.5 flex items-center gap-1.5 text-[12px] text-(--warm-slate-600)">
                  <Image src="/logo.svg" alt="" width={14} height={14} className="opacity-70" />
                  Boost your donation by 25% at no cost to you
                </span>
              </span>
            </label>

            {giftAid && (
              <div className="mt-4 pt-4 border-t border-amber-200/60 grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass} htmlFor="house-number">House no./name *</label>
                  <Input
                    id="house-number"
                    type="text"
                    autoComplete="address-line1"
                    value={donorHouseNumber}
                    onChange={(e) => setDonorHouseNumber(e.target.value)}
                    placeholder="e.g. 64 or Rose Cottage"
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="postcode">Postcode *</label>
                  <Input
                    id="postcode"
                    type="text"
                    autoComplete="postal-code"
                    value={donorPostcode}
                    onChange={(e) => setDonorPostcode(e.target.value.toUpperCase())}
                    placeholder="SW1A 1AA"
                    maxLength={8}
                    className={inputClass}
                    required
                  />
                </div>
              </div>
            )}
          </div>

          {/* Cover the fees — opt-in. Sits below Gift Aid because the
              TrusteeKit fee component depends on the GA toggle. */}
          {feeCover && feeCover.coverPence > 0 && (
            <div className="rounded-lg border border-(--warm-slate-200) bg-(--warm-slate-50) p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={coverFees}
                  onChange={(e) => setCoverFees(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-(--warm-slate-300) text-(--accent-gold) focus:ring-(--accent-gold)"
                />
                <span className="flex-1">
                  <span className="block text-[14px] font-medium text-(--warm-slate-800)">
                    Add {formatPence(feeCover.coverPence)} to cover transaction fees
                  </span>
                  <span className="mt-1.5 block text-[12px] text-(--warm-slate-600)">
                    {donationType === 'recurring'
                      ? `So ${charityName} receives your full £${effectiveAmount?.toFixed(effectiveAmount % 1 ? 2 : 0)} every year.`
                      : `So ${charityName} receives your full £${effectiveAmount?.toFixed(effectiveAmount % 1 ? 2 : 0)}.`}
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert className="bg-red-50 text-red-800 border-red-200">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={processing}
            className={cn(
              // --accent-contrast is set alongside a charity colour (white or
              // dark, whichever passes WCAG AA); white for the default gold.
              'w-full h-12 rounded-lg bg-(--accent-gold) text-[var(--accent-contrast,#fff)] font-medium text-[15px]',
              'hover:bg-(--accent-gold-hover) transition-colors',
              'disabled:bg-(--warm-slate-300) disabled:cursor-not-allowed',
              'flex items-center justify-center gap-2'
            )}
          >
            {processing && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel}
          </button>

          {!processing && (
            <p className="text-[11px] text-(--warm-slate-400) text-center">
              Powered by <a href="https://trusteekit.co.uk" target="_blank" className="hover:text-(--warm-slate-600) underline">TrusteeKit</a> · <a href="https://trusteekit.co.uk/why-direct-debit" target="_blank" className="hover:text-(--warm-slate-600) underline">Secure bank transfer via GoCardless</a>
            </p>
          )}
        </div>
      </div>

      {/* GoCardlessDropin is rendered in the processing overlay above, not here.
          Having it here would mount the GC SDK script twice if both paths rendered. */}
    </form>
  );
}
